/**
 * Acting-role resolution for multi-role users (BAL-340 role switcher).
 *
 * The RoleSwitcher's displayed default is the FIRST profile role when no
 * explicit `ballee-active-role` cookie is set — so the menu must follow the
 * same precedence or the chrome contradicts the switcher ("Acting as Dancer"
 * while admin sections render). Acting roles are the COMPLETE partition of
 * the sidebar: one role's menu at a time, and the switcher is the only
 * crossover point (navigation-architecture skill).
 */

export interface ActingRoleContext {
  isSuperAdmin: boolean;
  /** Client-portal membership — adds the flag-derived `client` acting role. */
  isClientUser?: boolean;
  activeRole: string | null;
  roles: Array<{ profileType: string }>;
}

/**
 * The ordered role tokens the user can act as: their professional_profiles
 * rows first (switcher order), then the flag-derived extras by dominant-role
 * precedence (superadmin > client — mirrors resolve-roles.ts), so a pure super
 * admin with no cookie still defaults to the admin menu. Mirrors the extraRoles
 * the RoleSwitcher offers and the tokens setActiveRoleAction accepts.
 */
function actingRoleOptions(w: ActingRoleContext): string[] {
  const options: string[] = [];
  const seen = new Set<string>();
  const push = (role: string) => {
    if (seen.has(role)) return;
    seen.add(role);
    options.push(role);
  };

  for (const role of w.roles) push(role.profileType);
  if (w.isSuperAdmin) push('superadmin');
  if (w.isClientUser) push('client');

  return options;
}

/**
 * The role the user is currently acting as, resolved among their profile
 * roles + flag-derived extras. The fallback MUST stay in sync with the
 * RoleSwitcher's displayed default (role-switcher.tsx) so chrome and
 * switcher never contradict each other.
 */
export function resolveActingRole(w: ActingRoleContext): string {
  const options = actingRoleOptions(w);

  if (w.activeRole && options.includes(w.activeRole)) {
    return w.activeRole;
  }

  // No (valid) explicit choice: super admins default to the admin menu —
  // it is their workday surface; acting as Dancer/Client is the opt-in
  // preview (2026-07-05 feedback). Everyone else defaults to their first
  // profile role.
  if (w.isSuperAdmin) {
    return 'superadmin';
  }

  return w.roles[0]?.profileType ?? options[0] ?? 'dancer';
}

export function isActingSuperAdmin(w: ActingRoleContext): boolean {
  return w.isSuperAdmin && resolveActingRole(w) === 'superadmin';
}
